import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../lib/api';
import Icon from '../components/Icon';

// ============================================================================
// TYPES
// ============================================================================

interface MetricsOverview {
  status: string;
  timestamp: string;
  window: string;
  latency: {
    p50_ms: number;
    p95_ms: number;
    p99_ms: number;
  };
  requests: {
    total: number;
    errors: number;
    error_rate: string;
  };
  by_service: Array<{ service: string; count: number }>;
}

interface LatencyBreakdown {
  window_minutes: number;
  breakdown: Array<{
    service: string;
    operation: string;
    p50_ms: number;
    p95_ms: number;
    p99_ms: number;
    count: number;
    error_count: number;
  }>;
}

interface TraceItem {
  trace_id: string;
  span_id: string;
  parent_span_id?: string;
  service: string;
  operation: string;
  duration_ms: number;
  status: 'ok' | 'error';
  attributes: Record<string, unknown>;
}

interface ErrorItem {
  trace_id: string;
  span_id: string;
  service: string;
  operation: string;
  error_type: string;
  error_message: string;
  stack_trace?: string;
  created_at: string;
}

interface ErrorSummary {
  window_minutes: number;
  summary: Array<{
    service: string;
    error_type: string;
    count: number;
    last_seen: string;
  }>;
}

// ============================================================================
// COMPONENTS
// ============================================================================

function StatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'blue',
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  trend?: 'up' | 'down' | 'neutral';
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={clsx('p-3 rounded-lg', colorClasses[color])}>
          <Icon name={icon} className="w-6 h-6" />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center text-sm">
          <Icon
            name={trend === 'up' ? 'trending_up' : trend === 'down' ? 'trending_down' : 'trending_flat'}
            className={clsx('w-4 h-4 mr-1', {
              'text-green-500': trend === 'down',
              'text-red-500': trend === 'up',
              'text-gray-400': trend === 'neutral',
            })}
          />
          <span className="text-gray-500">vs last hour</span>
        </div>
      )}
    </div>
  );
}

function LatencyBar({ value, max, label }: { value: number; max: number; label: string }) {
  const percentage = Math.min((value / max) * 100, 100);
  const getColor = () => {
    if (value < 100) return 'bg-green-500';
    if (value < 500) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-medium text-gray-500 w-8">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all', getColor())}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs font-mono text-gray-600 w-16 text-right">{value}ms</span>
    </div>
  );
}

function TraceRow({ trace, onSelect }: { trace: TraceItem; onSelect: () => void }) {
  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={onSelect}
    >
      <td className="px-4 py-3">
        <code className="text-xs text-blue-600 font-mono">
          {trace.trace_id.slice(0, 8)}...
        </code>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-600">{trace.service}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-gray-800 font-medium">{trace.operation}</span>
      </td>
      <td className="px-4 py-3">
        <span
          className={clsx('text-sm font-mono', {
            'text-green-600': trace.duration_ms < 100,
            'text-yellow-600': trace.duration_ms >= 100 && trace.duration_ms < 500,
            'text-red-600': trace.duration_ms >= 500,
          })}
        >
          {trace.duration_ms}ms
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', {
            'bg-green-100 text-green-800': trace.status === 'ok',
            'bg-red-100 text-red-800': trace.status === 'error',
          })}
        >
          {trace.status}
        </span>
      </td>
    </tr>
  );
}

function ErrorRow({ error }: { error: ErrorItem }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-3">
          <code className="text-xs text-blue-600 font-mono">
            {error.trace_id?.slice(0, 8) || 'N/A'}...
          </code>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-gray-600">{error.service}</span>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm font-medium text-red-600">{error.error_type}</span>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-gray-800 truncate max-w-xs block">
            {error.error_message}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="text-xs text-gray-400">
            {new Date(error.created_at).toLocaleTimeString()}
          </span>
        </td>
      </tr>
      {expanded && error.stack_trace && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-gray-50">
            <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono overflow-x-auto">
              {error.stack_trace}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function MetricsPage() {
  const [overview, setOverview] = useState<MetricsOverview | null>(null);
  const [breakdown, setBreakdown] = useState<LatencyBreakdown | null>(null);
  const [traces, setTraces] = useState<TraceItem[]>([]);
  const [errors, setErrors] = useState<ErrorItem[]>([]);
  const [errorSummary, setErrorSummary] = useState<ErrorSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'traces' | 'errors'>('overview');
  const [timeWindow, setTimeWindow] = useState(60);
  const [autoRefresh, setAutoRefresh] = useState(false); // Default OFF to reduce D1 reads
  const [selectedTrace, setSelectedTrace] = useState<string | null>(null);
  const [traceDetail, setTraceDetail] = useState<Record<string, unknown> | null>(null);

  // Fetch all metrics data
  const fetchMetrics = useCallback(async () => {
    try {
      const [overviewRes, breakdownRes, tracesRes, errorsRes, errorSummaryRes] = await Promise.all([
        api.get<MetricsOverview>('/api/metrics'),
        api.get<LatencyBreakdown>(`/api/metrics/latency/breakdown?window=${timeWindow}`),
        api.get<{ traces: TraceItem[] }>('/api/metrics/traces?limit=20'),
        api.get<{ errors: ErrorItem[] }>('/api/metrics/errors?limit=20'),
        api.get<ErrorSummary>(`/api/metrics/errors/summary?window=${timeWindow}`),
      ]);

      setOverview(overviewRes);
      setBreakdown(breakdownRes);
      setTraces(tracesRes.traces || []);
      setErrors(errorsRes.errors || []);
      setErrorSummary(errorSummaryRes);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      setError('Failed to load metrics. Make sure the observability tables are migrated.');
    } finally {
      setLoading(false);
    }
  }, [timeWindow]);

  // Initial fetch
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Auto-refresh (60 second interval to reduce D1 load)
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchMetrics, 60000); // Every 60 seconds
    return () => clearInterval(interval);
  }, [autoRefresh, fetchMetrics]);

  // Fetch trace detail
  const fetchTraceDetail = async (traceId: string) => {
    try {
      const detail = await api.get<Record<string, unknown>>(`/api/metrics/traces/${traceId}`);
      setTraceDetail(detail);
      setSelectedTrace(traceId);
    } catch (err) {
      console.error('Failed to fetch trace:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Icon name="hourglass_empty" className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <Icon name="error_outline" className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-lg font-semibold text-red-800">Error Loading Metrics</h2>
          <p className="mt-2 text-red-600">{error}</p>
          <button
            onClick={fetchMetrics}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Icon name="monitoring" className="w-8 h-8 text-blue-600" />
            System Metrics
          </h1>
          <p className="text-gray-500 mt-1">
            Real-time observability for the auction platform
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Time Window Selector */}
          <select
            value={timeWindow}
            onChange={(e) => setTimeWindow(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value={15}>Last 15 minutes</option>
            <option value={60}>Last 1 hour</option>
            <option value={360}>Last 6 hours</option>
            <option value={1440}>Last 24 hours</option>
          </select>

          {/* Auto-refresh Toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={clsx(
              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
              autoRefresh
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
            title={autoRefresh ? 'Click to disable auto-refresh' : 'Click to enable auto-refresh (every 60s)'}
          >
            <Icon
              name={autoRefresh ? 'sync' : 'sync_disabled'}
              className={clsx('w-4 h-4', autoRefresh && 'animate-spin')}
            />
            {autoRefresh ? 'Auto (60s)' : 'Auto-refresh OFF'}
          </button>

          {/* Manual Refresh */}
          <button
            onClick={fetchMetrics}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Icon name="refresh" className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {[
          { id: 'overview', label: 'Overview', icon: 'dashboard' },
          { id: 'traces', label: 'Traces', icon: 'timeline' },
          { id: 'errors', label: 'Errors', icon: 'error_outline' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            <Icon name={tab.icon} className="w-4 h-4" />
            {tab.label}
            {tab.id === 'errors' && errors.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full text-xs">
                {errors.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Requests"
              value={overview.requests.total.toLocaleString()}
              subtitle={`Last ${overview.window}`}
              icon="query_stats"
              color="blue"
            />
            <StatCard
              title="P50 Latency"
              value={`${overview.latency.p50_ms}ms`}
              subtitle="Median response time"
              icon="speed"
              color="green"
            />
            <StatCard
              title="P95 Latency"
              value={`${overview.latency.p95_ms}ms`}
              subtitle="95th percentile"
              icon="timer"
              color="orange"
            />
            <StatCard
              title="Error Rate"
              value={overview.requests.error_rate}
              subtitle={`${overview.requests.errors} errors`}
              icon="error_outline"
              color={overview.requests.errors > 0 ? 'red' : 'green'}
            />
          </div>

          {/* Percentile Legend */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="info" className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Understanding Percentiles (P50, P95, P99)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <div>
                    <span className="font-semibold">P50 (Median):</span> Half of requests are faster than this. Represents typical user experience.
                  </div>
                  <div>
                    <span className="font-semibold">P95:</span> 95% of requests are faster. Shows tail latency, what users feel during load or contention.
                  </div>
                  <div>
                    <span className="font-semibold">P99:</span> 99% are faster. Highlights worst-case performance (cold starts, spikes).
                  </div>
                </div>
                <p className="mt-2 text-xs text-blue-600">💡 Tip: Large gaps between P50 and P95/P99 indicate inconsistent performance worth investigating.</p>
              </div>
            </div>
          </div>

          {/* Latency Breakdown */}
          {breakdown && breakdown.breakdown.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Icon name="bar_chart" className="w-5 h-5 text-blue-600" />
                Latency by Operation
              </h3>
              <div className="space-y-4">
                {breakdown.breakdown.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-800">
                          {item.operation}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          ({item.service})
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {item.count} requests
                        {item.error_count > 0 && (
                          <span className="text-red-500 ml-2">
                            ({item.error_count} errors)
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <LatencyBar value={item.p50_ms} max={500} label="P50" />
                      <LatencyBar value={item.p95_ms} max={500} label="P95" />
                      <LatencyBar value={item.p99_ms} max={500} label="P99" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Distribution */}
          {overview.by_service.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Icon name="pie_chart" className="w-5 h-5 text-purple-600" />
                Requests by Service
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {overview.by_service.map((svc, idx) => (
                  <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">
                      {svc.count.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{svc.service}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Traces Tab */}
      {activeTab === 'traces' && (
        <div className="space-y-6">
          {/* Trace Detail Modal */}
          {selectedTrace && traceDetail && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-auto">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-semibold">
                    Trace: {selectedTrace.slice(0, 16)}...
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedTrace(null);
                      setTraceDetail(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <Icon name="close" className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  <pre className="text-sm font-mono bg-gray-50 p-4 rounded-lg overflow-auto">
                    {JSON.stringify(traceDetail, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {/* Traces Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Icon name="timeline" className="w-5 h-5 text-blue-600" />
                Recent Traces
              </h3>
            </div>
            {traces.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Icon name="inbox" className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No traces captured yet</p>
                <p className="text-sm mt-1">Traces will appear as requests are made</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Trace ID
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Service
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Operation
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {traces.map((trace) => (
                    <TraceRow
                      key={`${trace.trace_id}-${trace.span_id}`}
                      trace={trace}
                      onSelect={() => fetchTraceDetail(trace.trace_id)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Errors Tab */}
      {activeTab === 'errors' && (
        <div className="space-y-6">
          {/* Error Summary */}
          {errorSummary && errorSummary.summary.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Icon name="summarize" className="w-5 h-5 text-red-600" />
                Error Summary (Last {errorSummary.window_minutes} minutes)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {errorSummary.summary.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-red-50 border border-red-100 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-red-800">{item.error_type}</p>
                        <p className="text-sm text-red-600">{item.service}</p>
                      </div>
                      <span className="text-2xl font-bold text-red-700">{item.count}</span>
                    </div>
                    <p className="text-xs text-red-500 mt-2">
                      Last seen: {new Date(item.last_seen).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Errors Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Icon name="error_outline" className="w-5 h-5 text-red-600" />
                Recent Errors
              </h3>
            </div>
            {errors.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Icon name="check_circle" className="w-12 h-12 mx-auto mb-4 text-green-300" />
                <p>No errors captured</p>
                <p className="text-sm mt-1">Your system is running smoothly</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Trace ID
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Service
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Error Type
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Message
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {errors.map((err, idx) => (
                    <ErrorRow key={idx} error={err} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-400">
        <p>
          Data refreshes every 10 seconds when auto-refresh is enabled.{' '}
          <Link to="/architecture" className="text-blue-500 hover:underline">
            Learn about the observability architecture
          </Link>
        </p>
      </div>
    </div>
  );
}
